import { useState } from "react";
import { AlertTriangle, Archive, Trash2, Ban } from "lucide-react";
import { useDeletePreview, useDeleteExecute, type DeletableEntity } from "@/modules/admin/delete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * Le même écran de suppression partout : il dit d'abord ce qui dépend de l'objet,
 * puis ne propose que ce qui est réellement possible — archiver, annuler, ou
 * supprimer. Les décisions sont prises en base, jamais ici.
 */
export function DeleteDialog({ entity, id, open, onOpenChange, onDeleted, onArchived }: {
  entity: DeletableEntity; id: string; open: boolean; onOpenChange: (o: boolean) => void;
  onDeleted?: () => void; onArchived?: () => void;
}) {
  const { data: p, isLoading, error } = useDeletePreview(entity, id, open);
  const run = useDeleteExecute();
  const [reason, setReason] = useState("");
  const [typed, setTyped] = useState("");

  const close = () => { setReason(""); setTyped(""); onOpenChange(false); };
  const shortName = p ? p.label.replace(/^[^ ]+ /, "") : "";
  const mustType = Boolean(p?.sensitive);
  const typedOk = !mustType || typed.trim() === shortName.trim();
  const reasonOk = !p?.reason_required || reason.trim().length > 2;

  const act = (mode: "archive" | "delete") =>
    run.mutate({ entity, id, mode, reason: reason.trim() || undefined }, {
      onSuccess: () => { close(); (mode === "delete" ? onDeleted : onArchived)?.(); },
    });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-lg" data-testid="delete-dialog">
        <DialogHeader>
          <DialogTitle>{p?.label ?? "Supprimer"}</DialogTitle>
          <DialogDescription>Rien n'est fait avant que tu choisisses. Le CRM vérifie d'abord ce qui dépend de cet élément.</DialogDescription>
        </DialogHeader>

        {isLoading && <p className="text-sm text-muted-foreground">Vérification des dépendances…</p>}
        {error && <p role="alert" className="text-sm text-destructive">{(error as { message?: string }).message ?? "Vérification impossible."}</p>}

        {p && (
          <div className="space-y-4">
            {p.blockers.length > 0 && (
              <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm" data-testid="delete-blockers">
                <div className="mb-1 flex items-center gap-2 font-semibold text-destructive"><AlertTriangle className="h-4 w-4" />Suppression impossible</div>
                <p>{p.blockers.map((b) => b.label).join(", ")} {p.blockers.length === 1 ? "y est rattaché(e)" : "y sont rattachés"}. Les supprimer romprait le lien et fausserait l'historique. Détache-les d'abord, ou archive plutôt cet élément.</p>
              </div>
            )}

            {p.never && p.blockers.length === 0 && (
              <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm" data-testid="delete-never">
                <div className="mb-1 flex items-center gap-2 font-semibold"><Ban className="h-4 w-4" />Cet élément se conserve</div>
                <p>{p.never}</p>
              </div>
            )}

            {p.can_delete && p.cascade.length > 0 && (
              <div className="rounded-xl bg-muted/60 p-3 text-sm">
                <div className="mb-1 font-medium">Sera supprimé avec :</div>
                <ul className="list-inside list-disc text-muted-foreground">
                  {p.cascade.map((c) => <li key={c.label}>{c.count} {c.label}</li>)}
                </ul>
              </div>
            )}

            {(p.can_delete || p.can_archive) && p.reason_required && (
              <div className="space-y-1.5">
                <Label htmlFor="del-reason">Motif *</Label>
                <Textarea id="del-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex. créé par erreur, doublon, données d'essai" />
                <p className="text-xs text-muted-foreground">Conservé dans le journal d'audit avec ton nom et la date.</p>
              </div>
            )}

            {p.can_delete && mustType && (
              <div className="space-y-1.5">
                <Label htmlFor="del-typed">Recopie <span className="font-mono font-semibold">{shortName}</span> pour confirmer</Label>
                <Input id="del-typed" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
              </div>
            )}

            <div className="flex flex-wrap justify-end gap-2">
              <Button type="button" variant="ghost" onClick={close}>Retour</Button>
              {p.can_archive && (
                <Button type="button" variant="outline" disabled={run.isPending || !reasonOk} onClick={() => act("archive")}>
                  <Archive className="mr-2 h-4 w-4" />{p.archive_label ?? "Archiver"}
                </Button>
              )}
              {p.can_delete && (
                <Button type="button" variant="destructive" disabled={run.isPending || !typedOk || !reasonOk} onClick={() => act("delete")} data-testid="delete-confirm">
                  <Trash2 className="mr-2 h-4 w-4" />Supprimer définitivement
                </Button>
              )}
            </div>

            {p.can_archive && p.archive_help && <p className="text-xs text-muted-foreground">{p.archive_help}</p>}
            {p.can_delete && <p className="text-xs text-muted-foreground">Une copie est gardée 30 jours dans la corbeille (Paramètres → Corbeille), puis effacée pour de bon.</p>}
            {!p.can_delete && !p.can_archive && !p.never && p.blockers.length === 0 && (
              <p className="text-sm text-muted-foreground">Rien n'est possible ici : seul un administrateur peut supprimer.</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Bouton « Supprimer » cohérent partout, avec son écran. */
export function DeleteButton({ entity, id, label = "Supprimer", onDeleted, onArchived, size, variant = "ghost", className }: {
  entity: DeletableEntity; id: string; label?: string; onDeleted?: () => void; onArchived?: () => void;
  size?: "sm" | "default" | "icon"; variant?: "ghost" | "outline"; className?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {/* type="button" : le bouton vit parfois dans un formulaire (fiche produit) et ne doit rien envoyer. */}
      <Button type="button" variant={variant} size={size} className={`text-destructive ${className ?? ""}`} onClick={() => setOpen(true)}>
        <Trash2 className={size === "icon" ? "h-4 w-4" : "mr-2 h-4 w-4"} />{size === "icon" ? "" : label}
      </Button>
      <DeleteDialog entity={entity} id={id} open={open} onOpenChange={setOpen} onDeleted={onDeleted} onArchived={onArchived} />
    </>
  );
}
